import React, { useContext, useEffect, useState } from 'react';
import { format, addDays, startOfWeek } from 'date-fns';
import {
  ArrowRightCircleIcon,
  ArrowLeftCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserGroupIcon,
} from '@heroicons/react/20/solid';
import { AuthContext } from '../AuthContext';
import { LockClosedIcon } from '@heroicons/react/24/solid';

const Standby = () => {
  const { id } = useContext(AuthContext);
  const [allShifts, setAllShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchStaff = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/staffmanager', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch staff');
      const data = await response.json();
      setStaff(data.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchShifts = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/shifts', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch shifts');
      const data = await response.json();
      setAllShifts(data.data || []);
    } catch (error) {
      console.error('Error fetching shifts:', error);
    }
  };

  useEffect(() => {
    fetchStaff();
    fetchShifts();
  }, []);

  useEffect(() => {
    fetchShifts();
  }, [weekOffset]);

  const currentUser = staff.find(s => s.id === id);
  const userRole = currentUser?.Role || '';

  const startOfSelectedWeek = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset * 7);
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(startOfSelectedWeek, i));
  const weekDateStrings = weekDates.map(d => format(d, 'yyyy-MM-dd'));

  const selectedWeekShifts = allShifts.filter(shift => {
    const shiftDate = format(new Date(shift.ShiftDate), 'yyyy-MM-dd');
    return weekDateStrings.includes(shiftDate);
  });

  const roleShifts = selectedWeekShifts.filter(shift => {
    const shiftStaff = staff.find(s => s.id === shift.StaffId);
    return shiftStaff?.Role === userRole;
  });

  const getDayInfo = (dateStr) => {
    const shiftsForDay = roleShifts.filter(shift =>
      format(new Date(shift.ShiftDate), 'yyyy-MM-dd') === dateStr
    );
    if (shiftsForDay.length === 0) return null;

    const volunteersSet = new Set();
    shiftsForDay.forEach(shift => {
      let rawVolunteers = shift.StandByVolunteers;
      if (typeof rawVolunteers === 'string') {
        try {
          rawVolunteers = JSON.parse(rawVolunteers);
        } catch (e) {
          rawVolunteers = rawVolunteers.split(',').map(Number);
        }
      }
      if (rawVolunteers && Array.isArray(rawVolunteers)) {
        rawVolunteers.forEach(volId => volunteersSet.add(volId));
      }
    });

    const volunteers = Array.from(volunteersSet);
    const hasVolunteered = volunteers.includes(id);
    const isScheduled = selectedWeekShifts.some(shift =>
      shift.StaffId === id && format(new Date(shift.ShiftDate), 'yyyy-MM-dd') === dateStr
    );

    const probability = hasVolunteered
      ? Math.round(100 / (volunteers.length || 1))
      : Math.round(100 / (volunteers.length + 1));

    return {
      shiftsCount: shiftsForDay.length,
      volunteersCount: volunteers.length,
      volunteers,
      hasVolunteered,
      isScheduled,
      probability,
    };
  };

  const handleVolunteer = async (dateStr) => {
    const info = getDayInfo(dateStr);
    if (!info) return;

    if (info.hasVolunteered) {
      setErrorMessage('You have already volunteered for this day.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    const confirmed = window.confirm(
      `This is a commitment and cannot be reversed.\n\n` +
      `Day: ${format(new Date(dateStr), 'EEEE, MMM d')}\n` +
      `Shifts requiring standby: ${info.shiftsCount}\n` +
      `Current volunteers: ${info.volunteersCount}\n\n` +
      `Do you want to volunteer for standby cover?`
    );

    if (!confirmed) return;

    try {
      const shiftsToVolunteer = roleShifts.filter(shift =>
        format(new Date(shift.ShiftDate), 'yyyy-MM-dd') === dateStr
      );
      const shiftIds = shiftsToVolunteer.map(s => s.id);

      const response = await fetch('https://api.insightos.co.uk/api/volunteer-standby', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ShiftDate: dateStr,
          ShiftIds: shiftIds,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to volunteer');
      }

      setSuccessMessage(`You have volunteered for standby on ${format(new Date(dateStr), 'EEEE, MMM d')}.`);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchShifts();
    } catch (error) {
      console.error('Error volunteering:', error);
      setErrorMessage(error.message);
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handlePreviousWeek = () => setWeekOffset(weekOffset - 1);
  const handleNextWeek = () => setWeekOffset(weekOffset + 1);

  return (
    <div className="mt-8 mb-5 max-w-4xl mx-auto">
      {successMessage && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-xl shadow-md mb-5">
          <p className="font-bold">Success</p>
          <p>{successMessage}</p>
        </div>
      )}
      {errorMessage && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-xl shadow-md mb-5">
          <p className="font-bold">Error</p>
          <p>{errorMessage}</p>
        </div>
      )}

      <div className="flex items-center justify-center py-4 mb-6 bg-white rounded-2xl shadow-sm border border-gray-200">
        <button
          type="button"
          onClick={handlePreviousWeek}
          className="mr-3 rounded-full bg-red-600 p-1.5 text-white shadow-sm hover:bg-red-500 transition-colors"
        >
          <ArrowLeftCircleIcon className="size-7" />
        </button>
        <span className="text-lg font-semibold text-gray-900 px-4 py-1 bg-gray-100 rounded-lg">
          {format(startOfSelectedWeek, 'MMMM dd, yyyy')}
        </span>
        <button
          type="button"
          onClick={handleNextWeek}
          className="ml-3 rounded-full bg-emerald-500 p-1.5 text-white shadow-sm hover:bg-emerald-400 transition-colors"
        >
          <ArrowRightCircleIcon className="size-7" />
        </button>
      </div>

      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
        🛟 Standby Opportunities for {userRole}
      </h3>

      <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-md bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Day</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Shifts</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Volunteers</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Probability</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {weekDates.map((date, idx) => {
              const dateStr = weekDateStrings[idx];
              const info = getDayInfo(dateStr);
              
              if (!info) {
                return (
                  <tr key={dateStr}>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {format(date, 'EEEE, MMM d')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">—</td>
                    <td className="px-6 py-4 text-sm text-gray-300">—</td>
                    <td className="px-6 py-4 text-sm text-gray-300">—</td>
                    <td className="px-6 py-4 text-sm text-gray-300">—</td>
                  </tr>
                );
              }

              return (
                <tr key={dateStr} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {format(date, 'EEEE, MMM d')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    <span className="inline-flex items-center gap-1">
                      <CalendarIcon className="h-4 w-4 text-indigo-500" />
                      {info.shiftsCount}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    <span className="inline-flex items-center gap-1">
                      <UserGroupIcon className="h-4 w-4 text-indigo-500" />
                      {info.volunteersCount}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${info.probability > 50 ? 'bg-red-500' : info.probability > 20 ? 'bg-yellow-500' : 'bg-green-500'}`}
                          style={{ width: `${info.probability}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-medium text-gray-700">{info.probability}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {info.isScheduled ? (
                      <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-gray-600">
                        On shift
                      </span>
                    ) : info.hasVolunteered ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                        <LockClosedIcon className="h-4 w-4 text-green-500" />
                        Volunteered
                      </span>
                    ) : (
                      <button
                        onClick={() => handleVolunteer(dateStr)}
                        className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        Volunteer
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Standby;